import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { Delete as DeleteIcon, Replay as ReplayIcon } from '@material-ui/icons'
import { FlexHorizontal, Spacer, useToasts } from '@vestaboard/installables'
import React, { useCallback, useState } from 'react'

import { Alert } from '@material-ui/lab'
import { AlertDialog } from '../alert-dialog'
import { BOARD_SERVICE_BASE_URL } from '../../env'
import { useAuth0 } from '@auth0/auth0-react'

interface IDlqLastStatus {
  type: 'status' | 'error' | 'unknown'
  code?: number
  reason?: string
  detail?: string
}

interface IDlqEntry {
  id: number
  topic: string
  sink_title: string
  sink_url: string
  payload: unknown
  last_status: IDlqLastStatus
  attempts: number
  archived_at: string
  cursor: string
}

const formatLastStatus = (lastStatus: IDlqLastStatus) => {
  if (lastStatus.type === 'status') return `HTTP ${lastStatus.code}`
  if (lastStatus.type === 'error') return lastStatus.reason || 'error'
  return lastStatus.detail || 'unknown'
}

export const Dlq = () => {
  const { getAccessTokenSilently } = useAuth0()
  const { addToast } = useToasts()

  const [entries, setEntries] = useState<IDlqEntry[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rowActionId, setRowActionId] = useState<number | null>(null)
  const [discardDialog, setDiscardDialog] = useState<{ open: boolean; id?: number; topic?: string }>({
    open: false
  })
  const [detailEntry, setDetailEntry] = useState<IDlqEntry | null>(null)

  const fetchPage = useCallback(async (cursor: string | null, append: boolean) => {
    if (!BOARD_SERVICE_BASE_URL) {
      setError('Missing API base URL. Configure BOARD_SERVICE_ENDPOINT in root env.')
      return
    }

    if (append) {
      setLoadingMore(true)
    } else {
      setLoading(true)
      setError(null)
    }

    try {
      const token = await getAccessTokenSilently()
      const params = new URLSearchParams()
      params.set('limit', '50')
      if (cursor) {
        params.set('cursor', cursor)
      }

      const response = await fetch(`${BOARD_SERVICE_BASE_URL}/dlq?${params.toString()}`, {
        headers: {
          'X-Vestaboard-Token': token
        }
      })

      if (!response.ok) {
        let detail = `Request failed (${response.status})`
        try {
          const errBody = (await response.json()) as { error?: string }
          if (errBody.error) {
            detail = errBody.error
          }
        } catch {
          /* ignore */
        }
        throw new Error(detail)
      }

      const body = (await response.json()) as { entries: IDlqEntry[]; next_cursor: string | null }

      setEntries(current => (append ? [...current, ...body.entries] : body.entries))
      setNextCursor(body.next_cursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load DLQ entries')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [getAccessTokenSilently])

  React.useEffect(() => {
    fetchPage(null, false)
  }, [fetchPage])

  const performAction = async (id: number, action: 'replay' | 'discard') => {
    if (!BOARD_SERVICE_BASE_URL) return

    setRowActionId(id)

    try {
      const token = await getAccessTokenSilently()
      const response = await fetch(`${BOARD_SERVICE_BASE_URL}/dlq/${id}/${action}`, {
        method: 'POST',
        headers: {
          'X-Vestaboard-Token': token
        }
      })

      if (!response.ok) {
        let detail = `Request failed (${response.status})`
        try {
          const errBody = (await response.json()) as { error?: string }
          if (errBody.error) {
            detail = errBody.error
          }
        } catch {
          /* ignore */
        }
        throw new Error(detail)
      }

      addToast(action === 'replay' ? 'Entry queued for retry' : 'Entry discarded', {
        autoDismiss: true,
        appearance: 'success'
      })
      setEntries(current => current.filter(entry => entry.id !== id))
    } catch (err) {
      addToast(err instanceof Error ? err.message : `Failed to ${action} entry`, {
        autoDismiss: true,
        appearance: 'error'
      })
    } finally {
      setRowActionId(null)
    }
  }

  const handleDiscardClick = (id: number, topic: string) => {
    setDiscardDialog({ open: true, id, topic })
  }

  const handleDiscardConfirm = async () => {
    if (discardDialog.id !== undefined) {
      await performAction(discardDialog.id, 'discard')
    }
    setDiscardDialog({ open: false })
  }

  const handleDiscardCancel = () => {
    setDiscardDialog({ open: false })
  }

  return (
    <Box>
      <Typography variant='h4'>DLQ</Typography>
      <Spacer size='extraLarge' />

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>{error}</Alert>
      ) : entries.length ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Topic</TableCell>
                  <TableCell>Sink</TableCell>
                  <TableCell>Last Status</TableCell>
                  <TableCell>Attempts</TableCell>
                  <TableCell>Archived At</TableCell>
                  <TableCell style={{ width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Link component='button' onClick={() => setDetailEntry(entry)}>
                        {entry.topic}
                      </Link>
                    </TableCell>
                    <TableCell>{entry.sink_title}</TableCell>
                    <TableCell>
                      <Chip size='small' label={formatLastStatus(entry.last_status)} />
                    </TableCell>
                    <TableCell>{entry.attempts}</TableCell>
                    <TableCell>{new Date(entry.archived_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => performAction(entry.id, 'replay')}
                        disabled={rowActionId === entry.id}
                        size='small'
                        title='Retry'>
                        <ReplayIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDiscardClick(entry.id, entry.topic)}
                        disabled={rowActionId === entry.id}
                        size='small'
                        title='Archive'
                        color='secondary'>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Spacer size='large' />
          {nextCursor && (
            <FlexHorizontal>
              <Button onClick={() => fetchPage(nextCursor, true)} disabled={loadingMore} variant='outlined'>
                {loadingMore ? <CircularProgress size={20} /> : 'Load More'}
              </Button>
            </FlexHorizontal>
          )}
        </>
      ) : (
        <Alert severity='info'>The DLQ is empty.</Alert>
      )}

      <AlertDialog
        open={discardDialog.open}
        title='Archive Entry'
        description={`Are you sure you want to archive this "${discardDialog.topic}" entry? It will be permanently removed from the queue and will not be retried.`}
        onCancel={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
      />

      <Dialog open={!!detailEntry} onClose={() => setDetailEntry(null)} maxWidth='md' fullWidth>
        {detailEntry && (
          <>
            <DialogTitle>{detailEntry.topic}</DialogTitle>
            <DialogContent>
              <Typography variant='subtitle2'>Sink</Typography>
              <Typography variant='body2' gutterBottom>
                {detailEntry.sink_title} &mdash; {detailEntry.sink_url}
              </Typography>

              <Typography variant='subtitle2'>Last Status</Typography>
              <Typography variant='body2' gutterBottom>
                {formatLastStatus(detailEntry.last_status)}
              </Typography>

              <Typography variant='subtitle2'>Attempts</Typography>
              <Typography variant='body2' gutterBottom>
                {detailEntry.attempts}
              </Typography>

              <Typography variant='subtitle2'>Archived At</Typography>
              <Typography variant='body2' gutterBottom>
                {new Date(detailEntry.archived_at).toLocaleString()}
              </Typography>

              <Typography variant='subtitle2'>Payload</Typography>
              <Box
                component='pre'
                style={{
                  overflow: 'auto',
                  maxHeight: 320,
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4
                }}>
                {JSON.stringify(detailEntry.payload, null, 2)}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  )
}
